// GS.4 / GS.4.1 — Central de Entregáveis
//
// Rota: /membros/meu-projeto/materiais (auth-only via layout _authenticated).
//
// GS.4.1 — proteção em profundidade (não confia em UI escondida):
//   1. Requer flag `hamburgueria_gold_standard` ativa.
//   2. Requer projeto ativo do próprio usuário (RLS de getBusinessWorkspace).
//   3. Requer que o projeto esteja atrelado à metodologia piloto
//      (slug `hamburgueria-de-bairro-gold`).
//   4. Requer que o mapa bloco→missão exista (jornada configurada).
//   Falha em qualquer condição → redireciona para /membros/meu-projeto sem
//   revelar que há conteúdo restrito, sem consultar deliverables.
//
// Navegação por ativo: cada card usa `resolveDeliverableRoute` sobre o mapa
// canônico (getBlockMissionMap). Sem índices frágeis. Fallback registrado
// como analytics `deliverable_route_fallback` (best-effort).
//
// Isolamento: qualquer expansão sai atrás da mesma flag admin_only.

import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect } from "react";
import { getBusinessWorkspace } from "@/lib/workspace.functions";
import { getBlockMissionMap } from "@/lib/workspace.functions";
import {
  deriveDeliverables, deliverablesByGroup, DELIVERABLE_GROUP_META,
  STATUS_STYLES, deliverableActionLabel,
  type Deliverable, type DeliverableGroup,
} from "@/lib/deliverables";
import { resolveDeliverableRoute } from "@/lib/deliverable-navigation";
import { deriveGoldConclusion } from "@/lib/methodology/gold-conclusion";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { track } from "@/hooks/useAnalytics";
import { ArrowRight, FileText, AlertCircle, Clock, Printer, ShieldAlert } from "lucide-react";

const GOLD_METHODOLOGY_SLUG = "hamburgueria-de-bairro-gold";

export const Route = createFileRoute("/_authenticated/membros/meu-projeto/materiais")({
  head: () => ({
    meta: [
      { title: "Materiais do seu negócio — Projeto 101" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: MateriaisPage,
});

function MateriaisPage() {
  const navigate = useNavigate();
  const goldEnabled = useFeatureFlag("hamburgueria_gold_standard");
  const getWs = useServerFn(getBusinessWorkspace);
  const getMap = useServerFn(getBlockMissionMap);

  const wsQ = useQuery({ queryKey: ["business-workspace"], queryFn: () => getWs() });
  // Só busca o mapa quando a flag está ativa (evita consulta extra pra quem
  // não vai passar do gate).
  const mapQ = useQuery({
    queryKey: ["block-mission-map"],
    queryFn: () => getMap(),
    enabled: goldEnabled,
  });

  const loading = wsQ.isLoading || (goldEnabled && mapQ.isLoading);
  const project = wsQ.data?.project ?? null;
  const methodology = mapQ.data?.methodology ?? null;
  const map = mapQ.data?.map ?? null;

  // GS.4.1 — gate real. Se algo falhar, não renderiza dados, não expõe
  // detalhes técnicos, não revela que existe conteúdo restrito, redireciona.
  const authorized =
    !loading &&
    goldEnabled &&
    !!project &&
    methodology === GOLD_METHODOLOGY_SLUG &&
    !!map &&
    Object.keys(map).length > 0;

  const denied = !loading && !authorized;

  useEffect(() => {
    if (!denied) return;
    // Sinal opaco para diagnóstico; sem PII, sem motivo específico exposto ao usuário.
    try {
      track("deliverables_access_denied" as any, {
        metadata: {
          has_flag: goldEnabled,
          has_project: !!project,
          methodology_ok: methodology === GOLD_METHODOLOGY_SLUG,
          has_map: !!map && Object.keys(map ?? {}).length > 0,
        },
      });
    } catch { /* telemetria não deve bloquear redirect */ }
    navigate({ to: "/membros/meu-projeto", replace: true });
  }, [denied, goldEnabled, project, methodology, map, navigate]);

  if (loading || !authorized) {
    return (
      <div className="mx-auto max-w-5xl p-6">
        <div className="h-96 animate-pulse rounded-2xl bg-black/5" />
      </div>
    );
  }

  const grouped = deliverablesByGroup(wsQ.data?.workspace);
  const business = (wsQ.data?.business as any) || null;
  const groupOrder = (Object.keys(DELIVERABLE_GROUP_META) as DeliverableGroup[])
    .sort((a, b) => DELIVERABLE_GROUP_META[a].order - DELIVERABLE_GROUP_META[b].order);
  const allDeliverables = groupOrder.flatMap((g) => grouped[g]);
  const totalBlocked = allDeliverables.filter((d) => d.status === "blocked").length;
  const conclusion = deriveGoldConclusion(allDeliverables);
  const conclusionTone: Record<typeof conclusion.tone, string> = {
    positive: "border-emerald-200 bg-emerald-50 text-emerald-900",
    neutral: "border-sky-200 bg-sky-50 text-sky-900",
    warning: "border-amber-200 bg-amber-50 text-amber-900",
    critical: "border-red-200 bg-red-50 text-red-900",
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6 print:max-w-none print:p-0">
      <header className="rounded-3xl border border-[var(--navy)]/10 bg-gradient-to-br from-[var(--navy)]/[0.04] to-white p-6 lg:p-8 print:border-0 print:bg-white">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--navy)]/70">
          <FileText size={12} /> Materiais do seu negócio
        </div>
        <h1 className="mt-2 font-display text-2xl font-extrabold text-[var(--navy)] lg:text-3xl">
          O que você construiu até agora
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-[var(--gray-ink)]">
          Ativos práticos gerados a partir das suas decisões. Cada material referencia o bloco que o alimenta —
          revisar aqui reabre a decisão certa.
        </p>
        {business?.title && (
          <p className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
            Negócio: {business.title}
          </p>
        )}
        {totalBlocked > 0 && (
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-900">
            <ShieldAlert size={14} className="mt-0.5 shrink-0" />
            <span><strong>{totalBlocked} ativo(s) bloqueado(s).</strong> Resolva os pontos críticos antes de abrir.</span>
          </div>
        )}
        <div className={`mt-4 rounded-xl border p-4 text-xs ${conclusionTone[conclusion.tone]}`} data-testid="gold-conclusion" data-state={conclusion.state}>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="font-display text-sm font-extrabold">{conclusion.label}</p>
            <span className="text-[10px] font-semibold uppercase tracking-wide opacity-70">
              {conclusion.counts.ready}/{conclusion.counts.total} prontos · {conclusion.counts.partial} parcial · {conclusion.counts.blocked} bloqueado
            </span>
          </div>
          <p className="mt-1 font-medium">{conclusion.headline}</p>
          <p className="mt-1 opacity-90">{conclusion.detail}</p>
          {conclusion.reasons.length > 0 && (
            <ul className="mt-2 space-y-0.5 text-[11px] opacity-90">
              {conclusion.reasons.map((r, i) => (<li key={i}>• {r}</li>))}
            </ul>
          )}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3 print:hidden">
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 rounded-md border border-black/10 bg-white px-3 py-1.5 text-xs font-semibold text-[var(--navy)] hover:bg-[var(--gray-soft)]"
          >
            <Printer size={12} /> Versão para impressão
          </button>
          <Link
            to="/membros/meu-projeto"
            className="inline-flex items-center gap-1.5 rounded-md bg-[var(--navy)] px-3 py-1.5 text-xs font-semibold text-white"
          >
            Voltar para Meu Projeto
          </Link>
        </div>
      </header>

      {groupOrder.map((g) => {
        const items = grouped[g];
        if (!items.length) return null;
        const meta = DELIVERABLE_GROUP_META[g];
        const ready = items.filter((d) => d.status === "ready").length;
        return (
          <section key={g} className="space-y-3">
            <div className="flex items-end justify-between gap-3">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--gray-ink)]">Grupo {meta.order}</p>
                <h2 className="font-display text-xl font-extrabold text-[var(--navy)]">{meta.title}</h2>
                <p className="text-xs text-[var(--gray-ink)]">{meta.caption}</p>
              </div>
              <span className="rounded-full bg-[var(--gray-soft)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[var(--navy)]">
                {ready}/{items.length} prontos
              </span>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {items.map((d) => (
                <DeliverableCard key={d.id} d={d} map={map} />
              ))}
            </div>
          </section>
        );
      })}

      <p className="mt-4 text-center text-[11px] text-[var(--gray-ink)] print:hidden">
        Exportação em PDF individual será liberada na próxima onda.
      </p>
    </div>
  );
}

function DeliverableCard({ d, map }: { d: Deliverable; map: Record<string, string> | null }) {
  const navigate = useNavigate();
  const style = STATUS_STYLES[d.status];
  const route = resolveDeliverableRoute(d, map);
  const actionLabel = deliverableActionLabel(d);

  const onReview = () => {
    if (route.kind === "mission") {
      navigate({
        to: "/membros/meu-projeto/missao/$id",
        params: { id: route.missionId },
        search: {},
      });
      return;
    }
    // Fallback seguro: home do projeto (que usa resume canônico).
    try {
      track("deliverable_route_fallback" as any, {
        metadata: { deliverable_id: d.id, reason: route.reason },
      });
    } catch { /* noop */ }
    navigate({ to: "/membros/meu-projeto" });
  };

  return (
    <article className={`flex flex-col rounded-2xl border ${style.ring} bg-white p-5 shadow-sm`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">{d.phase}</p>
          <h2 className="mt-0.5 font-display text-lg font-extrabold text-[var(--navy)]">{d.title}</h2>
        </div>
        <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${style.chip}`}>
          {d.statusLabel}
        </span>
      </div>

      <p className="mt-2 text-sm text-[var(--navy)]">{d.summary}</p>

      {d.headlineMetrics && d.headlineMetrics.length > 0 && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          {d.headlineMetrics.map((m) => (
            <div key={m.label} className="rounded-lg bg-[var(--gray-soft)] px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">{m.label}</p>
              <p className="font-display text-sm font-extrabold text-[var(--navy)]">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {d.sections.length > 0 && (
        <details className="group mt-3 rounded-lg border border-black/5 bg-[var(--gray-soft)]/40 p-3 open:bg-white">
          <summary className="cursor-pointer text-[11px] font-semibold uppercase tracking-wide text-[var(--gray-ink)] hover:text-[var(--navy)]">
            Ver conteúdo consolidado
          </summary>
          <div className="mt-3 space-y-3">
            {d.sections.map((s) => (
              <div key={s.title}>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">{s.title}</p>
                <dl className="mt-1 grid gap-1 text-sm">
                  {s.rows.map((r, i) => (
                    <div key={i} className={r.long ? "" : "grid grid-cols-[160px_1fr] gap-2"}>
                      <dt className="text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">{r.label}</dt>
                      <dd className={`text-[var(--navy)] ${r.long ? "mt-0.5" : ""}`}>{r.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        </details>
      )}

      {d.risks && d.risks.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {d.risks.slice(0, 3).map((r) => {
            const critical = r.severity === "critical";
            return (
              <div
                key={r.key}
                className={`flex items-start gap-1.5 rounded-lg border p-2 text-xs ${
                  critical ? "border-red-200 bg-red-50 text-red-900" : "border-amber-200 bg-amber-50 text-amber-900"
                }`}
              >
                <ShieldAlert size={12} className="mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold uppercase tracking-wide text-[10px]">{critical ? "Crítico" : "Atenção"}</p>
                  <p>{r.title}{r.message ? ` — ${r.message}` : ""}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {d.gaps.length > 0 && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-amber-800">
            <AlertCircle size={12} /> Falta preencher
          </div>
          <ul className="mt-1.5 space-y-1 text-xs text-amber-900">
            {d.gaps.slice(0, 4).map((g, i) => (
              <li key={i}>• {g.label}</li>
            ))}
            {d.gaps.length > 4 && <li className="text-amber-800/70">e mais {d.gaps.length - 4} item(ns)…</li>}
          </ul>
        </div>
      )}

      <div className="mt-auto pt-4 flex flex-wrap items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1 text-[10px] text-[var(--gray-ink)]">
          <Clock size={10} /> {d.updatedAt ? new Date(d.updatedAt).toLocaleDateString("pt-BR") : "sem atualização"}
        </span>
        <button
          type="button"
          onClick={onReview}
          className="inline-flex items-center gap-1 rounded-md bg-[var(--navy)] px-3 py-1.5 text-xs font-semibold text-white print:hidden"
        >
          {actionLabel} <ArrowRight size={12} />
        </button>
      </div>
    </article>
  );
}
